import { useMemo } from 'react';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { CardItem, ObjectiveKpiCard } from '@bloomays-lib/ui.shared';
import { PurchaseOrderDashboardKpis } from '@bloomays-lib/types.shared';
import { convertDateToStringFormat } from '@bloomays-lib/utils.shared';
import {
  aggregatePurchaseOrderAmounts,
  computeCoveredHorizonKpi,
  monthDateForKey,
  DashboardPurchaseOrder,
} from '../../helpers/purchaseOrderDashboard';

/**
 * Mois filtrables en un clic depuis les KPI : le mois précédent (clôture / facturation)
 * et le mois en cours (TEC-3263).
 */
export type PurchaseOrderKpiMonth = 'monthMinus1' | 'current';

export type PurchaseOrderKpiCardsProps = {
  kpis: PurchaseOrderDashboardKpis;
  purchaseOrders: DashboardPurchaseOrder[];
  referenceDate: Date;
  onSelectCoverage: () => void;
  onSelectMissingMonth: (month: PurchaseOrderKpiMonth) => void;
};

const formatAmount = (value: number): string => `${Math.round(value).toLocaleString('fr-FR')} €`;

export const PurchaseOrderKpiCards = ({
  kpis,
  purchaseOrders,
  referenceDate,
  onSelectCoverage,
  onSelectMissingMonth,
}: PurchaseOrderKpiCardsProps) => {
  const { t } = useTranslation(['purchaseOrderDashboard']);

  const covered = useMemo(() => computeCoveredHorizonKpi(kpis), [kpis]);
  const amounts = useMemo(() => aggregatePurchaseOrderAmounts(purchaseOrders), [purchaseOrders]);

  const monthMinus1Label = convertDateToStringFormat(
    monthDateForKey(referenceDate, 'monthMinus1'),
    'MMM yy',
  ).toUpperCase();
  const currentMonthLabel = convertDateToStringFormat(
    monthDateForKey(referenceDate, 'current'),
    'MMM yy',
  ).toUpperCase();
  const percentConsumed = Math.round(amounts.consumedRatio * 100);
  const coveredPercent = covered.total > 0 ? (covered.covered / covered.total) * 100 : 0;
  const missingMonthMinus1Count = kpis.monthMinus1.missing.count;
  const missingCurrentMonthCount = kpis.currentMonth.missing.count;

  return (
    <CardsRow>
      <ObjectiveKpiCard
        data-testid="kpi-covered"
        accent="pink"
        icon={<CardItem variant="pink" size="small" emoji={<CheckCircleOutlinedIcon fontSize="small" />} />}
        title={t('kpiCoveredTitle')}
        valuePrimary={String(covered.covered)}
        valueSecondary={` / ${covered.total}`}
        progressPercent={coveredPercent}
        footer={t('kpiCoveredGap', { count: covered.gap })}
        footerAccent={covered.gap > 0}
        onClick={onSelectCoverage}
      />

      <ObjectiveKpiCard
        data-testid="kpi-missing-month-minus-1"
        accent="pink"
        icon={<CardItem variant="pink" size="small" emoji={<WarningAmberOutlinedIcon fontSize="small" />} />}
        title={t('kpiMissingPreviousMonthTitle', { month: monthMinus1Label })}
        valuePrimary={String(missingMonthMinus1Count)}
        valueSecondary=" miss."
        progressPercent={missingMonthMinus1Count > 0 ? 100 : 0}
        footer={t('kpiToCreate', { count: missingMonthMinus1Count })}
        onClick={() => onSelectMissingMonth('monthMinus1')}
      />

      <ObjectiveKpiCard
        data-testid="kpi-missing-current-month"
        accent="pink"
        icon={<CardItem variant="pink" size="small" emoji={<CalendarMonthOutlinedIcon fontSize="small" />} />}
        title={t('kpiMissingCurrentMonthTitle', { month: currentMonthLabel })}
        valuePrimary={String(missingCurrentMonthCount)}
        valueSecondary=" miss."
        progressPercent={missingCurrentMonthCount > 0 ? 100 : 0}
        footer={t('kpiToCreate', { count: missingCurrentMonthCount })}
        onClick={() => onSelectMissingMonth('current')}
      />

      <ObjectiveKpiCard
        data-testid="kpi-total"
        accent="blue"
        icon={<CardItem variant="blue" size="small" emoji={<InfoOutlinedIcon fontSize="small" />} />}
        title={t('kpiTotalTitle')}
        valuePrimary={formatAmount(amounts.engaged)}
        progressPercent={percentConsumed}
        footer={t('kpiTotalRemaining', { remaining: formatAmount(amounts.remaining), percent: percentConsumed })}
      />
    </CardsRow>
  );
};

export default PurchaseOrderKpiCards;

const CardsRow = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: 16,
  marginBottom: 16,
}));
